// Statsig integration for feature flags and experiments
import { StatsigClient } from '@statsig/js-client';
import { StatsigSessionReplayPlugin } from '@statsig/session-replay';
import { StatsigAutoCapturePlugin } from '@statsig/web-analytics';
import { track } from './analytics.js';
import { getEnvironment } from '../utils/environment';

let statsigClient: StatsigClient | null = null;
let statsigInitialized = false;

// Initialize Statsig
export async function initStatsig(userId: string) {
  if (statsigInitialized) return;

  try {
    const environment = getEnvironment();

    // Create Statsig client with plugins
    statsigClient = new StatsigClient(
      "client-TyzUews8UkKutz2M1wicP22sJQfaatmsjPXqj2pPHmL",
      {
        userID: userId,
        custom: {
          source: 'web2app',
          version: 'v5',
          timestamp: new Date().toISOString(),
          environment: environment
        }
      },
      {
        environment: {
          tier: environment === 'production' ? 'production' : 'development'
        },
        plugins: [
          new StatsigSessionReplayPlugin(),
          new StatsigAutoCapturePlugin(),
        ],
      }
    );

    // Initialize the client
    await statsigClient.initializeAsync();
    statsigInitialized = true;

    console.log(`✅ Statsig initialized in ${environment} mode with session replay and auto-capture`);

    // Track initialization
    track('statsig_initialized', {
      user_id: userId,
      environment: environment
    });
  } catch (error) {
    console.error('Failed to initialize Statsig:', error);
  }
}

// Check feature gate
export function checkGate(gateName: string): boolean {
  if (!statsigInitialized || !statsigClient) {
    console.warn('Statsig not initialized, returning default for gate:', gateName);
    return false;
  }

  const result = statsigClient.checkGate(gateName);

  // Track gate exposure
  track('gate_exposed', {
    gate_name: gateName,
    gate_value: result
  });

  return result;
}

// Get experiment variant
export function getExperiment(experimentName: string, parameterName: string = 'variant'): string {
  if (!statsigInitialized || !statsigClient) {
    console.warn('Statsig not initialized, returning default for experiment:', experimentName);
    return 'control';
  }

  const experiment = statsigClient.getExperiment(experimentName);
  const value = experiment.get(parameterName, 'control');

  // Track experiment exposure
  track('experiment_exposed', {
    experiment_name: experimentName,
    parameter_name: parameterName,
    parameter_value: value
  });

  return value;
}

// Get dynamic config
export function getConfig(configName: string): any {
  if (!statsigInitialized || !statsigClient) {
    console.warn('Statsig not initialized, returning empty config for:', configName);
    return {};
  }

  const config = statsigClient.getDynamicConfig(configName);
  return config.getValue() || {};
}

// Get layer
export function getLayer(layerName: string): any {
  if (!statsigInitialized || !statsigClient) {
    console.warn('Statsig not initialized, returning empty layer for:', layerName);
    return {};
  }

  const layer = statsigClient.getLayer(layerName);
  return layer;
}

// Log custom event to Statsig
export function logStatsigEvent(eventName: string, value: string | number | null = null, metadata: Record<string, any> = {}) {
  if (!statsigInitialized || !statsigClient) {
    console.warn('Statsig not initialized, cannot log event:', eventName);
    return;
  }

  statsigClient.logEvent(eventName, value, metadata);
}

// Update user properties
export async function updateStatsigUser(updates: any) {
  if (!statsigInitialized || !statsigClient) {
    console.warn('Statsig not initialized, cannot update user');
    return;
  }

  // Update user in Statsig
  await statsigClient.updateUser({
    userID: updates.userID || statsigClient.getCurrentUser()?.userID,
    ...updates,
    custom: {
      ...statsigClient.getCurrentUser()?.custom,
      ...(updates.custom || {})
    }
  });
}

// Manually trigger session replay (if needed)
export function startSessionReplay() {
  if (!statsigInitialized || !statsigClient) {
    console.warn('Statsig not initialized, cannot start session replay');
    return;
  }

  // Session replay starts automatically with the plugin
  console.log('Session replay is active');
}

// Feature flags for the app
export const FLAGS = {
  // Paywall experiments
  PAYWALL_VARIANT: () => getExperiment('paywall_layout', 'variant'), // 'A' or 'B'
  SHOW_TIMER: () => checkGate('show_countdown_timer'),
  TIMER_DURATION: () => getConfig('paywall_config').timer_duration || 600,

  // Flow experiments
  SKIP_SELFIE: () => checkGate('skip_selfie_capture'),
  SHOW_SOCIAL_PROOF: () => checkGate('show_social_proof'),

  // Pricing experiments
  DISCOUNT_PERCENTAGE: () => getConfig('pricing_config').discount_percentage || 33,
  SHOW_MONTHLY_OPTION: () => checkGate('show_monthly_pricing'),

  // UI experiments
  USE_ANIMATIONS: () => checkGate('use_animations'),
  BUTTON_STYLE: () => getExperiment('button_style', 'variant'), // 'gradient' or 'solid'
};

// Export for global access
(window as any).statsigFlags = FLAGS;
(window as any).statsigClient = () => statsigClient;